import React, { useState, useEffect, useRef } from 'react';
import { anamnesiAPI, anamnesiVociAPI, visiteAPI } from '../../services/api';
import SchedaFisiologica from './SchedaFisiologica';
import RiquadroAbitudini from './RiquadroAbitudini';
import StatoSalvataggio, { FraseRigaNuova } from '../StatoSalvataggio';
import { useAuthStore } from '../../store/useStore';
import { TriangleAlert, Pill, ScrollText, Users, ClipboardList, StickyNote, PersonStanding, Smile } from 'lucide-react';
import MappaAnatomica from './MappaAnatomica';
import TerapieInCorso from './TerapieInCorso';
import '../../styles/Anamnesi.css';
import '../../styles/AnamnesiVoci.css';
import '../../styles/SchedaPaziente.css';
import { annunciaAnamnesiCambiata, annunciaProcedureCambiate } from '../../utils/annunci';
import { messaggioDa } from '../../utils/notifiche';
import { useModificaVoce } from '../../utils/modificaVoce';
import { sipuoChiudere, conInvio } from '../../utils/salvaCampo';
import { useUscitaProtetta } from '../../utils/uscitaProtetta';

/**
 * AnamnesiTab
 *
 * L'anamnesi non e' piu' un blocco di testo unico ma un insieme di
 * voci datate e firmate: si vede chi ha annotato cosa e quando.
 * Nessuno modifica le voci altrui; una voce non piu' valida si
 * dichiara superata e resta nello storico.
 */

const SEZIONI = [
    { key: 'allergie',           titolo: 'Allergie',            Icona: TriangleAlert, gravita: true,
      aiuto: 'Sostanze, farmaci, materiali. Indicare la gravità della reazione.' },
    /* Le terapie in corso sono una tabella, un farmaco per riga: le
       disegna TerapieInCorso (migration 233). Restano in questo elenco
       per l'ordine e per i riquadri che le seguono. */
    { key: 'farmaci',            titolo: 'Terapie in corso',    Icona: Pill, tabella: true },
    /* «Patologie» e non piu' «Patologie pregresse» (24 settembre 2026):
       le diagnosi delle visite ci fanno nascere anche quelle in corso
       (migration 234). Il codice resta patologie_remote. */
    { key: 'patologie_remote',   titolo: 'Patologie',           Icona: ScrollText,
      aiuto: 'Patologie del passato non diagnosticate qui: interventi, ricoveri, malattie passate. Quelle diagnosticate in una visita compaiono da sole.' },
    { key: 'familiare',          titolo: 'Anamnesi familiare',  Icona: Users,
      aiuto: 'Patologie ricorrenti in famiglia.' },
    { key: 'note',               titolo: 'Note',                Icona: StickyNote,
      aiuto: 'Altre informazioni rilevanti.' },
];

/* I quattro riquadri della scheda fisiologica non stanno in blocco: ognuno
   va accanto alla sezione con cui si legge. Chi legge un'anamnesi non
   salta avanti e indietro fra due elenchi.

     terapie in corso     → parto e allattamento, poi (solo per le pazienti)
                            gravidanze, climaterio e menopausa
     patologie pregresse  → interventi chirurgici, poi abitudini (fumo e alcol)
     anamnesi familiare   → famiglia, poi lavoro

   La mappa sta qui e non sparsa nel corpo: se un domani cambia l'ordine
   delle sezioni, si guarda in un posto solo. */
const RIQUADRI_DOPO = {
    farmaci:          ['parto', 'gravidanze'],
    patologie_remote: ['interventi', 'abitudini'],
    familiare:        ['famiglia', 'lavoro'],
};

const GRAVITA = [
    { valore: '',         etichetta: 'non indicata' },
    { valore: 'lieve',    etichetta: 'lieve' },
    { valore: 'moderata', etichetta: 'moderata' },
    { valore: 'grave',    etichetta: 'grave' },
];

/* I campi di una voce in modifica: lo stato del riquadro si calcola da loro. */
const CAMPI_VOCE = ['testo', 'gravita', 'rilevata_il'];

const messaggioErrore = (e, ripiego) => e?.message || e?.response?.data?.message || ripiego;

const soloData = (v) => {
    if (!v) return null;
    const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : null;
};

const AnamnesiTab = ({ pazienteId, sessoPaziente, onApriVisita }) => {
    const [activeView, setActiveView] = useState('voci');
    const [voci, setVoci] = useState([]);
    const [annotazioni, setAnnotazioni] = useState([]);
    const [mostraStorico, setMostraStorico] = useState(false);
    const [loading, setLoading] = useState(true);
    const [errore, setErrore] = useState(null);

    const user = useAuthStore((state) => state.user);

    // Composizione nuova voce, per sezione
    const [inserimento, setInserimento] = useState(null); // { sezione, testo, gravita, rilevata_il }
    const [modifica, setModifica] = useState(null);       // { id, testo, gravita, rilevata_il }
    const [salvataggio, setSalvataggio] = useState(false);

    const carica = async () => {
        try {
            setLoading(true);
            const [vociRes, annRes] = await Promise.all([
                anamnesiVociAPI.getByPaziente(pazienteId, mostraStorico),
                anamnesiAPI.getAnnotazioni(pazienteId),
            ]);
            setVoci(vociRes.data?.data?.voci || []);
            if (annRes.data.success) setAnnotazioni(annRes.data.data.annotazioni);
        } catch (e) {
            setErrore(messaggioErrore(e, 'Errore nel caricamento dell\'anamnesi'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { carica(); /* eslint-disable-next-line */ }, [pazienteId, mostraStorico]);

    /** L'utente puo' modificare questa voce? Il backend ricontrolla. */
    const modificabile = (v) => {
        if (user?.ruolo === 'admin') return true;
        if (!v.medico_id) return false;
        return String(user?.medico_id) === String(v.medico_id);
    };

    const salvaNuova = async () => {
        if (!inserimento?.testo?.trim()) return;
        setErrore(null);
        try {
            setSalvataggio(true);
            await anamnesiVociAPI.crea(pazienteId, {
                sezione: inserimento.sezione,
                testo: inserimento.testo,
                gravita: inserimento.gravita || null,
                rilevata_il: inserimento.rilevata_il || null,
            });
            setInserimento(null);
            carica(); annunciaAnamnesiCambiata();
        } catch (e) {
            setErrore(messaggioErrore(e, 'Errore nel salvataggio'));
        } finally {
            setSalvataggio(false);
        }
    };

    /* LA MODIFICA DI UNA VOCE SI SALVA DA SOLA, campo per campo, quando si
       esce dal campo (regola di Alex del 24/09/2026): niente «Salva», si
       chiude con «Chiudi». La voce NUOVA resta «Aggiungi voce». */
    /* Se la scheda si smonta con la modifica aperta (cambio di linguetta della
       cartella, Ctrl K) non c'e' nessun blur: quello che e' scritto si salva
       comunque, se differisce da cio' che c'e' in archivio. */
    const modificaOra = useRef(null);
    modificaOra.current = modifica;
    const auto = useModificaVoce(annunciaAnamnesiCambiata, () => (modificaOra.current ? {
        testo: modificaOra.current.testo,
        gravita: modificaOra.current.gravita || null,
        rilevata_il: modificaOra.current.rilevata_il || null,
    } : null));
    /* LA VOCE NUOVA resta un pulsante («Aggiungi voce» crea una riga firmata),
       ma quello che e' scritto non si perde in silenzio: la domanda al cambio
       di schermata e l'avviso del browser alla chiusura, come per ogni modulo.
       Ricarica automatica: la ferma gia' la rete dei campi trattenuti. */
    const nuovaScritta = !!inserimento && !!(inserimento.testo.trim() || inserimento.gravita || inserimento.rilevata_il);
    useUscitaProtetta(nuovaScritta);
    const valoreVoce = (campo) => (campo === 'testo' ? modifica.testo : (modifica[campo] || null));
    const apriModifica = (v) => {
        const valori = { testo: v.testo, gravita: v.gravita || '', rilevata_il: (v.rilevata_il || '').slice(0, 10) };
        auto.inizia(v.id, valori);
        setModifica({ id: v.id, ...valori });
    };
    const chiudiModifica = () => {
        if (!sipuoChiudere(auto.inCorso, auto.nonSalvati.length)) return;
        setModifica(null); carica();
    };

    const supera = async (v) => {
        const note = window.prompt(
            `Segnalare come non più valida:\n"${v.testo}"\n\nMotivo (facoltativo):`
        );
        if (note === null) return;
        setErrore(null);
        try {
            await anamnesiVociAPI.supera(v.id, note || null);
            carica(); annunciaAnamnesiCambiata();
        } catch (e) {
            setErrore(messaggioErrore(e, 'Errore nell\'operazione'));
        }
    };

    const riattiva = async (v) => {
        setErrore(null);
        try {
            await anamnesiVociAPI.riattiva(v.id);
            carica(); annunciaAnamnesiCambiata();
        } catch (e) {
            setErrore(messaggioErrore(e, 'Errore nell\'operazione'));
        }
    };

    /* «Nuova visita per stessa patologia» (migration 234): la visita di
       oggi, collegata, con la diagnosi dell'ultima visita. Se oggi c'e'
       gia' una visita il server non ne crea una seconda e chiede: la
       domanda si fa qui e si richiama con la risposta. */
    const [creandoVisita, setCreandoVisita] = useState(null);
    const nuovaVisitaPerPatologia = async (v, risposta = {}) => {
        setErrore(null);
        setCreandoVisita(v.id);
        try {
            const r = await visiteAPI.create(pazienteId, { patologia_voce_id: v.id, ...risposta });
            const vid = r.data?.data?.visita_id;
            annunciaProcedureCambiate();
            carica();
            if (vid && onApriVisita) onApriVisita(vid);
        } catch (e) {
            const d = e?.response?.status === 409 ? e.response.data?.data?.domanda : null;
            if (d && d.tipo === 'collega_oggi') {
                if (window.confirm(messaggioDa(e))) await nuovaVisitaPerPatologia(v, { collega_oggi: true });
            } else if (d && d.tipo === 'crea_comunque') {
                if (window.confirm(messaggioDa(e))) await nuovaVisitaPerPatologia(v, { crea_comunque: true });
            } else {
                setErrore(messaggioDa(e, 'Non riesco a creare la visita'));
            }
        } finally {
            setCreandoVisita(null);
        }
    };

    const elimina = async (v) => {
        const domanda = v.sezione === 'farmaci'
            ? 'Eliminare questa terapia?\n\nUsa "Stop" se la terapia era corretta ma è stata sospesa: resterà nella tabella.'
            : 'Eliminare questa voce?\n\nUsa "Non più valida" se il dato era corretto ma è superato: resterà nello storico.';
        if (!window.confirm(domanda)) return;
        setErrore(null);
        try {
            await anamnesiVociAPI.cancella(v.id);
            carica(); annunciaAnamnesiCambiata();
        } catch (e) {
            setErrore(messaggioErrore(e, 'Errore nell\'eliminazione'));
        }
    };

    if (loading) return <div className="loading">Caricamento...</div>;

    const views = [
        { id: 'voci',  label: 'Anamnesi',      Icona: ClipboardList },
        { id: 'mappe', label: 'Mappe anatomiche', Icona: PersonStanding },
    ];

    const vociDi = (sezione) => voci.filter((v) => v.sezione === sezione);

    const autoreDi = (v) => {
        if (v.medico_cognome) return `Dr. ${v.medico_cognome}`;
        return 'autore non registrato';
    };

    return (
        <div className="anamnesi-container">
            <div className="anamnesi-header">
                <div className="view-tabs">
                    {views.map((view) => (
                        <button
                            key={view.id}
                            className={`view-tab ${activeView === view.id ? 'active' : ''}`}
                            onClick={() => setActiveView(view.id)}
                        >
                            <view.Icona size={15} strokeWidth={1.8} aria-hidden="true" />
                            {view.label}
                        </button>
                    ))}
                </div>
                {activeView === 'voci' && (
                    <label className="av-storico">
                        <input
                            type="checkbox"
                            checked={mostraStorico}
                            onChange={(e) => setMostraStorico(e.target.checked)}
                        />
                        <span>Mostra anche le voci superate</span>
                    </label>
                )}
            </div>

            {errore && <div className="av-errore">{errore}</div>}

            {activeView === 'voci' && (
                <SchedaFisiologica pazienteId={pazienteId}>
                  {(riquadri) => (
                <div className="av-sezioni">
                    {SEZIONI.map((s) => {
                        const elenco = vociDi(s.key);
                        const inInserimento = inserimento?.sezione === s.key;

                        return (
                          <React.Fragment key={s.key}>
                            {s.tabella ? (
                                <TerapieInCorso
                                    pazienteId={pazienteId}
                                    voci={elenco}
                                    modificabile={modificabile}
                                    autoreDi={autoreDi}
                                    onCambiata={carica}
                                    onErrore={setErrore}
                                    onElimina={elimina}
                                />
                            ) : (
                            <div className={`av-sezione ${s.key === 'allergie' ? 'av-sezione-critica' : ''}`}>
                                <div className="av-sezione-testa">
                                    <h3><s.Icona size={16} strokeWidth={1.8} aria-hidden="true" />{s.titolo}</h3>
                                    <button
                                        className="av-btn-aggiungi"
                                        onClick={() => setInserimento({ sezione: s.key, testo: '', gravita: '', rilevata_il: '' })}
                                        disabled={inInserimento}
                                    >
                                        ＋ Aggiungi
                                    </button>
                                </div>

                                {elenco.length === 0 && !inInserimento && (
                                    <p className="av-vuoto">Nessuna voce registrata.</p>
                                )}

                                {elenco.map((v) => {
                                    const inModifica = modifica?.id === v.id;
                                    const superata = v.stato === 'superata';

                                    if (inModifica) {
                                        return (
                                            <div key={v.id} className="av-voce av-voce-modifica" data-salva-da-solo>
                                                <textarea
                                                    value={modifica.testo}
                                                    onChange={(e) => setModifica({ ...modifica, testo: e.target.value })}
                                                    onBlur={(e) => auto.esci('testo', e.target.value)}
                                                    onKeyDown={conInvio((e) => auto.esci('testo', e.target.value))}
                                                    rows={2}
                                                    autoFocus
                                                />
                                                <div className="av-riga-campi">
                                                    {s.gravita && (
                                                        <select
                                                            value={modifica.gravita || ''}
                                                            onChange={(e) => { setModifica({ ...modifica, gravita: e.target.value }); auto.esci('gravita', e.target.value || null); }}
                                                        >
                                                            {GRAVITA.map((g) => (
                                                                <option key={g.valore} value={g.valore}>Gravità: {g.etichetta}</option>
                                                            ))}
                                                        </select>
                                                    )}
                                                    <input
                                                        type="date"
                                                        value={modifica.rilevata_il || ''}
                                                        onChange={(e) => setModifica({ ...modifica, rilevata_il: e.target.value })}
                                                        onBlur={(e) => auto.esci('rilevata_il', e.target.value || null)}
                                                        onKeyDown={conInvio((e) => auto.esci('rilevata_il', e.target.value || null))}
                                                        title="Data del rilievo"
                                                    />
                                                    <div className="av-azioni-modifica">
                                                        <button className="av-btn-annulla" onClick={chiudiModifica} disabled={auto.inCorso}>
                                                            Chiudi
                                                        </button>
                                                    </div>
                                                </div>
                                                <StatoSalvataggio campi={CAMPI_VOCE} stato={auto.stato} salvatoAlle={auto.salvatoAlle}
                                                    onRiprova={() => auto.erroriAperti.forEach((x) => auto.riprova(x.campo, valoreVoce(x.campo)))} />
                                                {auto.erroriAperti.map((x) => (
                                                    <div key={x.campo} className="av-errore-campo" role="alert">
                                                        <span>Non salvato: {x.messaggio}. Il testo è ancora nel campo.</span>
                                                        <button type="button" className="av-link"
                                                                onClick={() => auto.riprova(x.campo, valoreVoce(x.campo))}>
                                                            Riprova
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        );
                                    }

                                    return (
                                        <div key={v.id} className={`av-voce ${superata ? 'av-voce-superata' : ''}`}>
                                            <div className="av-voce-corpo">
                                                {v.gravita && (
                                                    <span className={`av-gravita av-gravita-${v.gravita}`}>{v.gravita}</span>
                                                )}
                                                <span className="av-testo">{v.testo}</span>
                                            </div>
                                            <div className="av-voce-piede">
                                                <span className="av-firma">
                                                    {autoreDi(v)}
                                                    {v.visita_origine_id
                                                        ? ` · dalla diagnosi della visita del ${soloData(v.origine_data) || '—'}`
                                                        : (soloData(v.rilevata_il) ? ` · rilevata il ${soloData(v.rilevata_il)}` : '')}
                                                    {` · inserita il ${soloData(v.created_at) || '—'}`}
                                                </span>
                                                <div className="av-voce-azioni">
                                                    {!superata && modificabile(v) && (
                                                        <button
                                                            className="av-link"
                                                            onClick={() => apriModifica(v)}
                                                        >
                                                            Modifica
                                                        </button>
                                                    )}
                                                    {!superata && (
                                                        <button className="av-link" onClick={() => supera(v)}>
                                                            Non più valida
                                                        </button>
                                                    )}
                                                    {superata && (
                                                        <button className="av-link" onClick={() => riattiva(v)}>
                                                            Riattiva
                                                        </button>
                                                    )}
                                                    {modificabile(v) && (
                                                        <button className="av-link av-link-pericolo" onClick={() => elimina(v)}>
                                                            Elimina
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                            {s.key === 'patologie_remote' && (v.n_visite > 0 || (!superata && user?.medico_id)) && (
                                                <div className="av-patologia-visite">
                                                    {v.n_visite > 0 && (
                                                        <span>
                                                            Ultima visita {soloData(v.ultima_data) || '—'}
                                                            {` · ${v.n_visite === 1 ? '1 visita' : `${v.n_visite} visite`}`}
                                                            {onApriVisita && v.ultima_visita_id && (
                                                                <button className="av-link" onClick={() => onApriVisita(v.ultima_visita_id)}>
                                                                    Apri l'ultima visita
                                                                </button>
                                                            )}
                                                        </span>
                                                    )}
                                                    {/* Solo chi e' collegato a un medico: le stesse regole di
                                                        «Nuova visita». Il server lo ricontrolla. */}
                                                    {!superata && user?.medico_id && (
                                                        <button
                                                            className="av-btn-aggiungi"
                                                            onClick={() => nuovaVisitaPerPatologia(v)}
                                                            disabled={creandoVisita === v.id}
                                                        >
                                                            {creandoVisita === v.id ? 'Apertura…' : 'Nuova visita per stessa patologia'}
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                            {superata && (
                                                <div className="av-superata-nota">
                                                    Superata il {soloData(v.superata_il) || '—'}
                                                    {v.superata_da_cognome ? ` da Dr. ${v.superata_da_cognome}` : ''}
                                                    {v.superata_note ? ` — ${v.superata_note}` : ''}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}

                                {inInserimento && (
                                    <div className="av-voce av-voce-nuova">
                                        <FraseRigaNuova pulsante="Aggiungi voce" />
                                        <textarea
                                            value={inserimento.testo}
                                            onChange={(e) => setInserimento({ ...inserimento, testo: e.target.value })}
                                            rows={2}
                                            placeholder={s.aiuto}
                                            autoFocus
                                        />
                                        <div className="av-riga-campi">
                                            {s.gravita && (
                                                <select
                                                    value={inserimento.gravita}
                                                    onChange={(e) => setInserimento({ ...inserimento, gravita: e.target.value })}
                                                >
                                                    {GRAVITA.map((g) => (
                                                        <option key={g.valore} value={g.valore}>Gravità: {g.etichetta}</option>
                                                    ))}
                                                </select>
                                            )}
                                            <input
                                                type="date"
                                                value={inserimento.rilevata_il}
                                                onChange={(e) => setInserimento({ ...inserimento, rilevata_il: e.target.value })}
                                                title="Data del rilievo, se diversa da oggi"
                                            />
                                            <div className="av-azioni-modifica">
                                                <button className="av-btn-annulla" onClick={() => setInserimento(null)} disabled={salvataggio}>
                                                    Annulla
                                                </button>
                                                <button className="av-btn-salva" onClick={salvaNuova} disabled={salvataggio || !inserimento.testo.trim()}>
                                                    {salvataggio ? 'Salvataggio…' : 'Aggiungi voce'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            )}

                            {/* I riquadri a campi che vanno dopo questa
                                sezione: parto, interventi, famiglia,
                                lavoro. La mappa e' in testa al file. */}
                            {(RIQUADRI_DOPO[s.key] || []).map((nome) => (
                                <React.Fragment key={nome}>
                                    {/* Le abitudini non sono della scheda
                                        fisiologica: hanno il loro storico
                                        e si salvano da sole. */}
                                    {nome === 'abitudini'
                                        ? <RiquadroAbitudini pazienteId={pazienteId} />
                                        : riquadri[nome]}
                                </React.Fragment>
                            ))}
                          </React.Fragment>
                        );
                    })}

                    {/* Qui c'era il riquadro «Dalle procedure», con «Porta
                        nell'anamnesi»: tolto il 24 settembre 2026 (Alex),
                        insieme alla rotta che lo alimentava. Il suo posto lo
                        prendono le patologie nate dalle diagnosi delle visite.
                        I testi restano nelle procedure. */}
                    <p className="av-piede">
                        Ogni voce resta di chi l'ha scritta. Se un dato non è più valido segnalalo
                        come "non più valida": resterà consultabile nello storico invece di sparire.
                    </p>

                    {riquadri.azioni}
                </div>
                  )}
                </SchedaFisiologica>
            )}

            {activeView === 'mappe' && (
                <MappaAnatomica
                    pazienteId={pazienteId}
                    sessoPaziente={sessoPaziente}
                    annotazioni={annotazioni}
                    onCreata={async (dati) => {
                        await anamnesiAPI.createAnnotazione(pazienteId, dati);
                        carica();
                    }}
                    onEliminata={async (id) => {
                        await anamnesiAPI.deleteAnnotazione(id);
                        carica();
                    }}
                />
            )}
        </div>
    );
};

export default AnamnesiTab;
